import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Download, ExternalLink, BookOpen, ChevronDown, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function XDFLibraryPanel() {
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const r = await fetch(`${API_URL}/api/xdf-library/catalog`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        setCatalog(d);
      } catch (e) {
        setError(e.message);
      }
    })();
  }, []);

  const handleDownload = async (chassis, rom) => {
    setDownloading(`${chassis}/${rom}`);
    try {
      const token = localStorage.getItem('auth_token');
      const r = await fetch(
        `${API_URL}/api/xdf-library/fetch/${encodeURIComponent(chassis)}/${encodeURIComponent(rom)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = rom;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(`Download failed: ${e.message}`);
    } finally {
      setDownloading(null);
    }
  };

  if (error) {
    return (
      <Card className="bg-zinc-900 border-red-500/40" data-testid="xdf-library-error">
        <CardContent className="p-6 text-red-400">Failed to load catalog: {error}</CardContent>
      </Card>
    );
  }
  if (!catalog) {
    return (
      <Card className="bg-zinc-900 border-zinc-700" data-testid="xdf-library-loading">
        <CardContent className="p-6 text-zinc-400">Loading XDF library…</CardContent>
      </Card>
    );
  }

  const chassisEntries = Object.entries(catalog.chassis || {});
  const totalFiles = chassisEntries.reduce((acc, [, info]) => acc + (info.files?.length || 0), 0);

  return (
    <div className="space-y-4" data-testid="xdf-library-panel">
      <Card className="bg-gradient-to-br from-zinc-900 to-black border-cyan-500/30 shadow-[0_0_30px_rgba(6,182,212,0.08)]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-cyan-300">
            <BookOpen className="w-5 h-5" />
            BMW XDF Library
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Community-maintained XDF map definitions for {chassisEntries.length} BMW chassis families —{' '}
            <span className="text-cyan-400">{totalFiles}</span> ROM definitions fetched on-demand from{' '}
            <a
              href={catalog.source_repo}
              target="_blank"
              rel="noopener noreferrer"
              className="text-cyan-400 hover:underline inline-flex items-center gap-1"
              data-testid="xdf-library-source-link"
            >
              dmacpro91/BMW-XDFs
              <ExternalLink className="w-3 h-3" />
            </a>
          </CardDescription>
        </CardHeader>
        <CardContent className="text-xs text-zinc-500 border-t border-zinc-800 pt-3">
          <p>
            <strong className="text-zinc-300">For:</strong> tuners using TunerPro RT with N54/N55/B58/S55/S58/S63/N20 BMWs. These XDFs are
            paired with MHD+ flashing — see the repo for usage docs.
          </p>
          <p className="mt-1">
            <strong className="text-zinc-300">MSV80 / N52 / E90 NA owners:</strong> use the AI Tuner Pro Verified XDF on the
            Tunes tab. The repo above doesn&apos;t have MSV80 coverage (NA platform).
          </p>
          <p className="mt-1 text-zinc-600">{catalog.attribution} · Snapshot {catalog.fetched_snapshot}</p>
        </CardContent>
      </Card>

      {chassisEntries.map(([chassis, info]) => {
        const isOpen = !!expanded[chassis];
        return (
          <Card key={chassis} className="bg-zinc-900/60 border-zinc-700" data-testid={`xdf-chassis-${chassis.replace(/\s+/g, '-')}`}>
            <button
              type="button"
              className="w-full flex items-center justify-between p-4 hover:bg-zinc-800/40 transition-colors text-left"
              onClick={() => setExpanded((p) => ({ ...p, [chassis]: !p[chassis] }))}
              data-testid={`xdf-chassis-toggle-${chassis.replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center gap-3">
                {isOpen ? <ChevronDown className="w-4 h-4 text-cyan-400" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
                <div>
                  <div className="text-cyan-300 font-semibold">{chassis}</div>
                  <div className="text-xs text-zinc-500">
                    {info.engines?.join(' · ')} · {info.years}
                  </div>
                </div>
              </div>
              <Badge variant="outline" className="border-cyan-500/40 text-cyan-300 text-xs">
                {info.files?.length} ROMs
              </Badge>
            </button>
            {isOpen && (
              <CardContent className="border-t border-zinc-800 pt-4 space-y-3">
                {info.chassis?.length > 0 && (
                  <div className="text-xs text-zinc-400">
                    <strong className="text-zinc-300">Chassis:</strong> {info.chassis.join(', ')}
                  </div>
                )}
                {info.notes && <div className="text-xs text-amber-400/80 italic">{info.notes}</div>}
                <div className="space-y-2">
                  {info.files.map((f) => {
                    const key = `${chassis}/${f.name}`;
                    const isDownloading = downloading === key;
                    return (
                      <div
                        key={f.name}
                        className="flex items-center justify-between gap-3 p-2 rounded bg-black/30 border border-zinc-800 hover:border-cyan-500/30 transition-colors"
                        data-testid={`xdf-file-${chassis.replace(/\s+/g, '-')}-${f.name}`}
                      >
                        <div>
                          <div className="text-sm font-mono text-zinc-200">{f.name}</div>
                          <div className="text-xs text-zinc-500">{f.label}</div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isDownloading}
                          className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 text-xs"
                          onClick={() => handleDownload(chassis, f.name)}
                          data-testid={`xdf-download-${chassis.replace(/\s+/g, '-')}-${f.name}`}
                        >
                          <Download className="w-3 h-3 mr-1.5" />
                          {isDownloading ? 'Fetching…' : 'Download'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
}
